/* eslint-disable no-useless-escape */

import type { EvacZoneStatus, RegionEvacZoneStyle } from './shared/types';

export const WATCHDUTY_SCHEME = 'watchduty://';

export const TERMS_OF_SERVICE = 'https://www.watchduty.org/legal/eula';
export const PRIVACY_POLICY = 'https://www.watchduty.org/legal/privacy-policy';
export const TEAMS_TERMS_OF_SERVICE =
  'https://www.watchduty.org/legal/teams-terms-conditions';

/**
 * Query parameter name for tracking ID used in cross-platform user identification.
 * Using 'tr' to obscure that this is a tracking ID.
 */
export const TRACKING_ID_PARAM = 'tr';

/**
 * Release flag keys used throughout the application
 * These correspond to flag_key values from the backend /api/v1/deploy_flags/ endpoint
 */
export type ReleaseFlagKey =
  | 'river_gauges'
  | 'locations_v2'
  | 'aqi_stations_layer'
  | 'weather_station_precipitation'
  | 'enable_critical_alerts'
  | 'use_report_warning_words_api'
  | 'enable_external_layer_management'
  | 'show_external_lightning_layer_in_frontend'
  | 'dark_mode'
  | 'show_heat_detections_from_gis_in_frontend'
  | 'show_ngfs_detections_in_frontend'
  | 'show_smoke_forecast_layer_in_frontend'
  | 'show_timeline_layers_in_frontend'
  | 'show_planet_labs_satellite_basemap_in_frontend'
  | 'limit_alert_cameras_in_frontend'
  | 'show_report_photos_on_map'
  | 'aircraft_layer_use_vendor_cdn'
  | 'aircraft_layer_use_deck_gl'
  | 'pin_drop_improvements'
  | 'unstructured_evacuations'
  | 'internal_tab_on_incidents'
  | 'use_alternate_static_tile_cache_domain'
  | 'places_server_source_of_truth';

export const APPLE_APP_STORE_ID = '1574452924';

export const AppStoreLinks = {
  iosBannerCampaign: `https://apps.apple.com/app/apple-store/id${APPLE_APP_STORE_ID}?pt=123122876&ct=App%20Banner%20in%20Mobile%20Browsers&mt=8`,
  ios: `https://apps.apple.com/us/app/watch-duty-wildfire/id${APPLE_APP_STORE_ID}`,
  android: 'https://play.google.com/store/apps/details?id=org.watchduty.app',
} as const;

export const UsStatesAbbr = {
  Arizona: 'AZ',
  Alabama: 'AL',
  Alaska: 'AK',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
} as const;

export const DEFAULT_LAT = 0;

export const DEFAULT_LON = 0;

// Random limit - allows field to resize with content up to this limit
export const TEXTAREA_MAX_ROWS = 100;

/**
 * Placeholder shown in the UI when a value is missing, null, or empty.
 * Use this instead of hardcoding a bare '-' so the "no value" display stays
 * consistent across the app.
 */
export const EMPTY_DATA_VALUE = '-';

export const EvacZoneStatuses = {
  orders: 'orders',
  warnings: 'warnings',
  advisories: 'advisories',
  shelterInPlace: 'shelter_in_place',
} as const;

export const EvacZoneStyles = {
  default: 'order_warning_advisory',
  readySetGo: 'ready_set_go',
  withLevels: 'ready_set_go_with_levels',
  reverseColors: 'ready_set_go_reverse_colors',
  readySetShelter: 'ready_set_shelter',
} as const;

export const LocationTypes = {
  shelter: 'shelter',
  evacShelter: 'evac_shelter',
  animalShelter: 'animal_shelter', // for backward compatibility
  smallAnimalShelter: 'small_animal_shelter',
  info: 'info',
  general: 'general',
  food: 'food', // for backward compatibility
  meals: 'meals',
  firstAid: 'first_aid',
  temporaryEvacPoint: 'temporary_evac_point',
  sandBags: 'sand_bags',
  water: 'water',
  farmersMarket: 'farmers_market',
  foodPantry: 'food_pantry',
  largeAnimalShelter: 'large_animal_shelter',
  maskDistribution: 'mask_distribution',
  communityDistribution: 'community_distribution',
} as const;

export const LinkTypes = {
  phone: 'phone',
  website: 'website',
  email: 'email',
} as const;

export const GeoEventTypes = {
  wildfire: 'wildfire',
  location: 'location',
  flooding: 'flooding',
  hazard: 'hazard',
} as const;

export const ReportableGeoEventTypes = {
  wildfire: 'wildfire',
  flooding: 'flooding',
  hazard: 'hazard',
} as const;

export type ReportableGeoEventType =
  (typeof ReportableGeoEventTypes)[keyof typeof ReportableGeoEventTypes];

export const HazardTypes = {
  damLeveeFailure: 'dam_levee_failure',
  hazmat: 'hazmat',
  landslide: 'landslide',
  roadWashout: 'road_washout',
  standingWater: 'standing_water',
  swiftWater: 'swift_water',
  other: 'other',
} as const;

// There are a few more but not currently supplied to the client
export type ExternalSource =
  | 'adeq'
  | 'aircraft_detection'
  | 'alert_west'
  | 'alert_west_detection'
  | 'arkansas_forestry'
  | 'cal_fire'
  | 'chp'
  | 'dnr'
  | 'fire_guard'
  | 'firesponse_fl'
  | 'firesponse_ga'
  | 'firesponse_ky'
  | 'firesponse_nc'
  | 'firesponse_sc'
  | 'firesponse_tn'
  | 'firesponse_va'
  | 'florida_forest_service'
  | 'mfc'
  | 'pfirs'
  | 'pulsepoint'
  | 'n5_shield'
  | 'ngfs'
  | 'nifc'
  | 'nws_alert'
  | 'nws_lsr'
  | 'odf'
  | 'noaa_river_gauge'
  | 'spot_forecast'
  | 'synoptic_precipitation'
  | 'texas_am'
  | 'wea'
  | 'wea_pbs_warn'
  | 'wi_dnr'
  | 'wildcad';

export const ExternalGeoNameMap: Record<ExternalSource, string> = {
  adeq: 'ADEQ',
  aircraft_detection: 'Aircraft Circling',
  alert_west: 'Camera hit',
  alert_west_detection: 'ALERT West',
  arkansas_forestry: 'AR Forestry',
  cal_fire: 'CAL FIRE',
  chp: 'CHP',
  dnr: 'DNR',
  fire_guard: 'Fire Guard',
  firesponse_fl: 'FFS',
  firesponse_ga: 'GFC',
  firesponse_ky: 'KDF',
  firesponse_nc: 'NCFS',
  firesponse_sc: 'SCFC',
  firesponse_tn: 'TDF',
  firesponse_va: 'VA DOF',
  florida_forest_service: 'FFS',
  mfc: 'MFC',
  n5_shield: 'N5',
  ngfs: 'NGFS',
  nifc: 'NIFC',
  nws_alert: 'NWS Alert',
  nws_lsr: 'NWS LSR',
  odf: 'ODF',
  pfirs: 'PFIRS',
  pulsepoint: 'Pulsepoint',
  noaa_river_gauge: 'River Gauge Alert',
  spot_forecast: 'Spot Weather',
  synoptic_precipitation: 'Precipitation',
  texas_am: 'Texas A&M',
  wea: 'WEA',
  wea_pbs_warn: 'WEA (PBS Warn)',
  wi_dnr: 'WI DNR',
  wildcad: 'WildCAD',
};

// Taken from Yup's URL validation regex but modified to not require the protocol:
// https://github.com/jquense/yup/blob/089ce8bfa8137e0dd1886bd40301b96bcc6deef7/src/string.ts#L24
export const UrlRegex =
  /^(((https|ftp):)?\/\/)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i;

export const PhoneNumberRegex = /^(1|)?(\d{3})(\d{3})(\d{4})$/;

export const PhoneSMSRegex = /^(\d{5,6})$/;

export const ABORT_SHARE_ERROR_STRING = 'Share canceled';

export const MAP_DOM_ELEMENT_ID = 'map-container';

export const SEARCH_DRAWER_ID = 'search-bottom-sheet';

export const SEARCH_DRAWER_WIDTH = 375;

const SEARCH_CARD_MIN_SNAP_PX = 74;

export const getSearchCardMinSnapPx = (): number => {
  const safeArea =
    getComputedStyle(document.documentElement).getPropertyValue(
      '--safeAreaInsetBottom',
    ) || '0px';

  // The bottom safe area on iOS
  const safeAreaPx = parseInt(safeArea.replace('px', ''), 10) / 3;

  return SEARCH_CARD_MIN_SNAP_PX + safeAreaPx;
};

export type EvacZoneStyleStatusMapping<T = string> = Record<
  RegionEvacZoneStyle,
  Record<EvacZoneStatus, T>
>;

export const EVAC_ZONE_STROKE_COLOR = '#000000';
export const SHELTER_IN_PLACE_COLOR = '#A750FF';
export const ORDERS_COLOR = '#E25345';
export const WARNINGS_COLOR = '#FBC02D';
export const ADVISORIES_DEFAULT_COLOR = '#6393F2';
export const ADVISORIES_RSG_COLOR = '#509F4E';
export const EVAC_ZONE_NORMAL_COLOR = '#1976d2';

export const REPORT_MAP_OBFUSCATION = {
  COLOR: '#6393F2',
  MAX_ZOOM: 17,
  MIN_ZOOM: 14,
  OPACITY: 0.5,
  RADIUS_METERS: 100,
};

export const EVAC_ZONE_STATUS_ENABLED: EvacZoneStyleStatusMapping<boolean> = {
  [EvacZoneStyles.default]: {
    orders: true,
    warnings: true,
    advisories: true,
    shelter_in_place: false,
  },
  [EvacZoneStyles.readySetGo]: {
    orders: true,
    warnings: true,
    advisories: true,
    shelter_in_place: false,
  },
  [EvacZoneStyles.withLevels]: {
    orders: true,
    warnings: true,
    advisories: true,
    shelter_in_place: false,
  },
  [EvacZoneStyles.reverseColors]: {
    orders: true,
    warnings: true,
    advisories: true,
    shelter_in_place: false,
  },
  [EvacZoneStyles.readySetShelter]: {
    orders: true,
    warnings: true,
    advisories: false,
    shelter_in_place: true,
  },
} as const;

export const EVAC_ZONE_LAYER_COLORS: EvacZoneStyleStatusMapping = {
  [EvacZoneStyles.default]: {
    orders: ORDERS_COLOR,
    warnings: WARNINGS_COLOR,
    advisories: ADVISORIES_DEFAULT_COLOR,
    shelter_in_place: SHELTER_IN_PLACE_COLOR,
  },
  [EvacZoneStyles.readySetGo]: {
    orders: ORDERS_COLOR,
    warnings: WARNINGS_COLOR,
    advisories: ADVISORIES_RSG_COLOR,
    shelter_in_place: SHELTER_IN_PLACE_COLOR,
  },
  [EvacZoneStyles.withLevels]: {
    orders: ORDERS_COLOR,
    warnings: WARNINGS_COLOR,
    advisories: ADVISORIES_RSG_COLOR,
    shelter_in_place: SHELTER_IN_PLACE_COLOR,
  },
  [EvacZoneStyles.reverseColors]: {
    orders: ADVISORIES_RSG_COLOR,
    warnings: WARNINGS_COLOR,
    advisories: ORDERS_COLOR,
    shelter_in_place: SHELTER_IN_PLACE_COLOR,
  },
  [EvacZoneStyles.readySetShelter]: {
    orders: ORDERS_COLOR,
    warnings: WARNINGS_COLOR,
    advisories: ADVISORIES_RSG_COLOR,
    shelter_in_place: SHELTER_IN_PLACE_COLOR,
  },
} as const;

export const EVAC_ZONE_LABEL_TRANS_KEYS: EvacZoneStyleStatusMapping = {
  [EvacZoneStyles.default]: {
    orders: 'evacZones.default.orders',
    warnings: 'evacZones.default.warnings',
    advisories: 'evacZones.default.advisories',
    shelter_in_place: 'evacZones.default.shelterInPlace',
  },
  [EvacZoneStyles.readySetGo]: {
    orders: 'evacZones.readySetGo.orders',
    warnings: 'evacZones.readySetGo.warnings',
    advisories: 'evacZones.readySetGo.advisories',
    shelter_in_place: 'evacZones.readySetGo.shelterInPlace',
  },
  [EvacZoneStyles.withLevels]: {
    orders: 'evacZones.withLevels.orders',
    warnings: 'evacZones.withLevels.warnings',
    advisories: 'evacZones.withLevels.advisories',
    shelter_in_place: 'evacZones.reverseColors.shelterInPlace',
  },
  [EvacZoneStyles.reverseColors]: {
    orders: 'evacZones.reverseColors.orders',
    warnings: 'evacZones.reverseColors.warnings',
    advisories: 'evacZones.reverseColors.advisories',
    shelter_in_place: 'evacZones.reverseColors.shelterInPlace',
  },
  [EvacZoneStyles.readySetShelter]: {
    orders: 'evacZones.default.orders',
    warnings: 'evacZones.default.warnings',
    advisories: 'evacZones.default.advisories',
    shelter_in_place: 'evacZones.default.shelterInPlace',
  },
} as const;

export const ORDERS_PATTERN = 'EvacOrderPattern';
export const ORDERS_REVERSE_PATTERN = 'EvacReverseOrderPattern';
export const WARNINGS_PATTERN = 'EvacWarningPattern';
export const ADVISORIES_DEFAULT_PATTERN = 'EvacAdvisoryBluePattern';
export const ADVISORIES_GREEN_PATTERN = 'EvacAdvisoryGreenPattern';
export const ADVISORIES_REVERSE_PATTERN = 'EvacReverseAdvisoryPattern';
export const SHELTER_IN_PLACE_PATTERN = 'EvacShelterInPlacePattern';

export const EVAC_ZONE_LAYER_PATTERNS: EvacZoneStyleStatusMapping = {
  [EvacZoneStyles.default]: {
    orders: ORDERS_PATTERN,
    warnings: WARNINGS_PATTERN,
    advisories: ADVISORIES_DEFAULT_PATTERN,
    shelter_in_place: SHELTER_IN_PLACE_PATTERN,
  },
  [EvacZoneStyles.readySetGo]: {
    orders: ORDERS_PATTERN,
    warnings: WARNINGS_PATTERN,
    advisories: ADVISORIES_GREEN_PATTERN,
    shelter_in_place: SHELTER_IN_PLACE_PATTERN,
  },
  [EvacZoneStyles.withLevels]: {
    orders: ORDERS_PATTERN,
    warnings: WARNINGS_PATTERN,
    advisories: ADVISORIES_GREEN_PATTERN,
    shelter_in_place: SHELTER_IN_PLACE_PATTERN,
  },
  [EvacZoneStyles.reverseColors]: {
    orders: ORDERS_REVERSE_PATTERN,
    warnings: WARNINGS_PATTERN,
    advisories: ADVISORIES_REVERSE_PATTERN,
    shelter_in_place: SHELTER_IN_PLACE_PATTERN,
  },
  [EvacZoneStyles.readySetShelter]: {
    orders: ORDERS_PATTERN,
    warnings: WARNINGS_PATTERN,
    advisories: ADVISORIES_DEFAULT_PATTERN,
    shelter_in_place: SHELTER_IN_PLACE_PATTERN,
  },
} as const;

export const COORDINATE_DISPLAY_FRACTION_DIGITS = 6;

export const INCIDENT_MARKER_MOVE_CONFIRM_THRESHOLD_MILES = 1;

export type RouterEntityDrawer =
  | 'wildfire'
  | 'location'
  | 'flooding'
  | 'hazard'
  | 'camera'
  | 'gauge'
  | 'weatherStation'
  | 'aqi'
  | 'alert'
  | 'place';

export const ROUTER_ENTITY_TO_ROUTE_PATH_MAP: Record<
  RouterEntityDrawer,
  string
> = {
  wildfire: '/i/:geoEventId',
  location: '/location/:locationId',
  flooding: '/i/:geoEventId',
  hazard: '/i/:geoEventId',
  camera: '/camera/:cameraId',
  gauge: '/gauge/:gaugeId',
  weatherStation: '/weather_station/:stationId',
  aqi: '/aqi/:sensorId',
  alert: '/alert/:alertId',
  place: '/place/:placeId',
};

// The map view with coordinates in the URL (`/@:lat,:lng[,:zoom]`).
export const MAP_COORDINATES_ROUTE_PATHS = ['/@:lat,:lng,:zoom', '/@:lat,:lng'];

// A dropped pin at those coordinates (`/@:lat,:lng[,:zoom]/pin`). The `/pin`
// suffix distinguishes a pin from a plain coordinate/viewport URL.
export const MAP_PIN_ROUTE_PATHS = ['/@:lat,:lng,:zoom/pin', '/@:lat,:lng/pin'];

// Matches a pin URL on any map page, including prefixed pages like the
// progression model (`/i/:geoEventId/models/:modelId/@:lat,:lng[,:zoom]/pin`).
export const MAP_PIN_ROUTE_MATCH_PATHS = MAP_PIN_ROUTE_PATHS.map(
  (path) => `*${path}`,
);

export const EXTERNAL_GEO_EVENT_DETAILS_ROUTE_PATH = '/s/:externalGeoEventId';

export const INCIDENT_VIEW_REPORT_ROUTE_PATH = '/i/:geoEventId/reports/:id';
export const INCIDENT_EDIT_REPORT_ROUTE_PATH =
  '/incident/:geoEventId/reports/:reportId/edit';

export const FLOODING_ADD_REPORT_ROUTE_PATH =
  '/flooding/:geoEventId/reports/create';

export const HAZARD_ADD_REPORT_ROUTE_PATH =
  '/hazard/:geoEventId/reports/create';

export const MODEL_PROVIDER_PROFILE_ROUTE_PATH =
  '/i/:geoEventId/models/provider/:source';

export const WILDFIRE_PROGRESSION_MODEL_ROUTE_PATH =
  '/i/:geoEventId/models/:modelId';

export const MODERATE_SUBMISSION_ROUTE_PATH = '/reports/submission/:reportId';

export const WEATHER_OBSERVATIONS_POLL_INTERVAL = 1000 * 60 * 5; // 5 minutes

// Debounce for reacting to element resize events (e.g. map, charts).
export const RESIZE_DEBOUNCE_MS = 200;

export const WEATHER_STATION_STALENESS_HOURS = 3;

export const DRAWER_HEIGHT_CSS_VAR = '--drawer-height';
export const DRAWER_HEIGHT_CSS_VALUE = `var(${DRAWER_HEIGHT_CSS_VAR}, 0px)`;

// MUI Toolbar height at the sm+ breakpoint (desktop).
export const TOOLBAR_HEIGHT_DESKTOP_PX = 64;

export const DOWNLOAD_BANNER_HEIGHT_PX = 86;
export const DOWNLOAD_APP_BANNER_OFFSET_CSS_VAR =
  '--download-app-banner-offset';
export const DOWNLOAD_APP_BANNER_OFFSET_CSS_VALUE = `var(${DOWNLOAD_APP_BANNER_OFFSET_CSS_VAR}, 0px)`;

// Space `PersistentStatusAlert` takes at the bottom of the map, if shown.
export const STATUS_ALERT_OFFSET_CSS_VAR = '--status-alert-offset';
export const STATUS_ALERT_OFFSET_CSS_VALUE = `var(${STATUS_ALERT_OFFSET_CSS_VAR}, 0px)`;
